export type ExtractedAuthSession = {
  rawUser: any | null;
  accessToken: string | null;
  refreshToken: string | null;
};

const isObject = (v: unknown): v is Record<string, any> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const hasUserShape = (v: unknown): v is Record<string, any> => {
  if (!isObject(v)) return false;
  // Heuristic: backend user objects almost always include at least one of these.
  return (
    v.id !== undefined ||
    typeof v.email === 'string' ||
    typeof v.username === 'string' ||
    v.profile_incomplete !== undefined ||
    v.needs_profile_update !== undefined
  );
};

const pickFirstUser = (...candidates: unknown[]): any | null => {
  for (const c of candidates) {
    if (hasUserShape(c)) return c;
  }
  return null;
};

const pickFirstTokens = (...candidates: unknown[]): Record<string, any> | null => {
  for (const c of candidates) {
    if (!isObject(c)) continue;
    if (c.access || c.refresh || c.access_token || c.refresh_token) return c;
  }
  return null;
};

/**
 * Extracts a consistent `{ rawUser, accessToken, refreshToken }` tuple from
 * the various auth response shapes used across the app.
 */
export const extractAuthSession = (data: any): ExtractedAuthSession => {
  // Common shapes seen in this codebase / backend:
  // - { user: { ... , tokens: { access, refresh } }, message }
  // - { data: { ...userFields, tokens }, tokens }
  // - { data: { user: { ... }, tokens }, tokens }
  // - { user: { ... }, tokens }
  const rawUser = pickFirstUser(data?.user, data?.data?.user, data?.data, data);

  const tokens =
    pickFirstTokens(
      data?.tokens,
      data?.data?.tokens,
      data?.user?.tokens,
      data?.data?.user?.tokens,
      rawUser?.tokens,
    ) ?? {};

  const accessToken =
    tokens.access ??
    tokens.access_token ??
    data?.token ??
    data?.access ??
    data?.access_token ??
    null;

  const refreshToken =
    tokens.refresh ?? tokens.refresh_token ?? data?.refresh ?? data?.refresh_token ?? null;

  return {
    rawUser,
    accessToken: accessToken ? String(accessToken) : null,
    refreshToken: refreshToken ? String(refreshToken) : null,
  };
};
